import React, { useState, useEffect } from 'react';
import { Building2, Plus, Search, Edit, Trash2, Users, X, SlidersHorizontal } from 'lucide-react';
import { useCompanies } from '../../hooks/useCompanies';
import { getCompanyLogoUrl } from '../../utils/imageUtils';
import CompanyForm from './CompanyForm';
import CompanyUsers from './CompanyUsers';
import Pagination from '../common/Pagination';

const PAGE_SIZE = 10;

const CompaniesModule = () => {
  const { companies, loading, error, loadCompanies, deleteCompany } = useCompanies();

  const [showForm, setShowForm]           = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [showUsers, setShowUsers]         = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm]       = useState('');
  const [filterEstado, setFilterEstado]   = useState('all');
  const [showFilters, setShowFilters]     = useState(false);
  const [page, setPage]                   = useState(1);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const handleCreate       = () => { setEditingCompany(null); setShowForm(true); };
  const handleEdit         = (c) => { setEditingCompany(c); setShowForm(true); };
  const handleManageUsers  = (c) => { setSelectedCompany(c); setShowUsers(true); };
  const handleFormClose    = (reload) => { setShowForm(false); setEditingCompany(null); if (reload) loadCompanies(); };
  const handleUsersClose   = () => { setShowUsers(false); setSelectedCompany(null); };

  const handleDelete = async (id, nombre) => {
    if (!window.confirm(`¿Desactivar la compañía "${nombre}"?`)) return;
    const result = await deleteCompany(id);
    if (result.success) loadCompanies();
    else alert(result.error);
  };

  // Resetear página al cambiar filtros
  const handleSearch  = (val) => { setSearchTerm(val);  setPage(1); };
  const handleEstado  = (val) => { setFilterEstado(val); setPage(1); };

  const filtered = companies.filter(c => {
    const q = searchTerm.toLowerCase();
    const matchSearch = !q ||
      c.nombre?.toLowerCase().includes(q) ||
      c.rnc?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q);
    const matchEstado = filterEstado === 'all' || c.estado === filterEstado;
    return matchSearch && matchEstado;
  });

  const activas      = companies.filter(c => c.estado === 'activa').length;
  const inactivas    = companies.filter(c => c.estado === 'inactiva').length;
  const totalPages   = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated    = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (showForm)  return <CompanyForm  company={editingCompany}  onClose={handleFormClose} />;
  if (showUsers) return <CompanyUsers company={selectedCompany} onClose={handleUsersClose} />;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Cargando compañías...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
          {filtered.length} de {companies.length}
        </span>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={15} />
          Nueva compañía
        </button>
      </div>

      {/* ── Search + Filter bar ─────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, RNC o email..."
            value={searchTerm}
            onChange={e => handleSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          />
          {searchTerm && (
            <button
              onClick={() => handleSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors ${
            showFilters || filterEstado !== 'all'
              ? 'border-blue-400 text-blue-600 bg-blue-50'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'
          }`}
        >
          <SlidersHorizontal size={15} />
          Filtros
          {filterEstado !== 'all' && <span className="w-2 h-2 rounded-full bg-blue-500" />}
        </button>

        <div className="hidden sm:flex items-center gap-2 ml-auto">
          <span className="text-xs text-gray-400">
            <span className="font-semibold text-green-600">{activas}</span> activas
          </span>
          <span className="text-gray-200">|</span>
          <span className="text-xs text-gray-400">
            <span className="font-semibold text-red-500">{inactivas}</span> inactivas
          </span>
        </div>
      </div>

      {/* ── Filter panel (colapsable) ────────────────────────────── */}
      {showFilters && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <select
            value={filterEstado}
            onChange={e => handleEstado(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="all">Todos los estados</option>
            <option value="activa">Activas</option>
            <option value="inactiva">Inactivas</option>
          </select>
          {filterEstado !== 'all' && (
            <button
              onClick={() => handleEstado('all')}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <X size={12} /> Limpiar
            </button>
          )}
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────────── */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {/* ── Empty state ─────────────────────────────────────────── */}
      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Building2 className="w-10 h-10 text-gray-200 mb-3" />
          <p className="text-sm text-gray-400">
            {searchTerm ? 'Sin resultados para esa búsqueda' : 'No hay compañías registradas'}
          </p>
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────── */}
      {filtered.length > 0 && (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Compañía</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell">RNC</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden lg:table-cell">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden lg:table-cell">Teléfono</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Estado</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {paginated.map(company => (
                <tr
                  key={company.id}
                  onClick={() => handleEdit(company)}
                  className="hover:bg-blue-50/40 cursor-pointer transition-colors group"
                >
                  {/* Logo + Nombre */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg border border-gray-100 bg-gray-50 flex items-center justify-center shrink-0 overflow-hidden">
                        {company.logo ? (
                          <img
                            src={getCompanyLogoUrl(company.logo)}
                            alt={company.nombre}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Building2 size={14} className="text-gray-300" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{company.nombre}</p>
                        {company.nombre_comercial && (
                          <p className="text-xs text-gray-400 truncate">{company.nombre_comercial}</p>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3 text-gray-500 hidden md:table-cell">
                    {company.rnc || <span className="text-gray-300">—</span>}
                  </td>

                  <td className="px-4 py-3 text-gray-500 hidden lg:table-cell max-w-[180px] truncate">
                    {company.email || <span className="text-gray-300">—</span>}
                  </td>

                  <td className="px-4 py-3 text-gray-500 hidden lg:table-cell">
                    {company.telefono || <span className="text-gray-300">—</span>}
                  </td>

                  {/* Estado */}
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      company.estado === 'activa'
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-600'
                    }`}>
                      {company.estado === 'activa' ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>

                  {/* Acciones */}
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleEdit(company)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit size={15} />
                      </button>
                      <button
                        onClick={() => handleManageUsers(company)}
                        className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Usuarios"
                      >
                        <Users size={15} />
                      </button>
                      {company.estado === 'activa' && (
                        <button
                          onClick={() => handleDelete(company.id, company.nombre)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Desactivar"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            totalPages={totalPages}
            total={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
};

export default CompaniesModule;
